/**
 * TanStack Query error handling configuration
 */

import type { DefaultOptions } from "@tanstack/react-query";
import { parseQueryError } from "./enhanced-parser";
import { ErrorCategory } from "./types";

/**
 * Calculate exponential backoff with jitter
 * @param attemptIndex - 0-indexed attempt number
 * @returns delay in milliseconds
 */
function getRetryDelay(attemptIndex: number): number {
	// Base delay: 1s, 2s, 4s
	const baseDelay = Math.min(1000 * 2 ** attemptIndex, 8000);

	// Add jitter (±25%)
	const jitter = baseDelay * 0.25 * (Math.random() * 2 - 1);

	return baseDelay + jitter;
}

/**
 * Determine if a failed query should be retried
 * @param failureCount - Number of times query has failed
 * @param error - The error that occurred
 * @returns whether to retry
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
	// Max 3 retries
	if (failureCount >= 3) {
		return false;
	}

	// Parse error to determine if retryable
	const appError = parseQueryError(error);

	// Only retry network and server errors
	return appError.retryable;
}

/**
 * Default options for TanStack Query with error handling
 */
export const queryConfig: DefaultOptions = {
	queries: {
		// Retry configuration
		retry: shouldRetry,
		retryDelay: getRetryDelay,

		// Cache configuration
		staleTime: 60 * 1000, // 1 minute
		gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)

		// Refetch configuration
		refetchOnWindowFocus: false,
		refetchOnMount: true,
		refetchOnReconnect: true,

		// Throw errors to Error Boundary (only for unhandled errors)
		throwOnError: false,
	},

	mutations: {
		// Mutations should not auto-retry by default
		// Use useMutationWithToast for automatic error handling
		retry: false,

		// Throw errors to Error Boundary (only for unhandled errors)
		throwOnError: false,

		// Network mode - retry network errors
		networkMode: "online",
	},
};

/**
 * Global query/mutation error handler — wired to QueryCache and MutationCache.
 * Called after all retries are exhausted. Reports server errors (5xx) to Sentry
 * in production. Does not show toasts — components handle their own error display.
 */
export function onQueryError(error: unknown) {
	const appError = parseQueryError(error);

	// Log in development
	if (import.meta.env.DEV) {
		console.error("[Query Error]", {
			message: appError.message,
			category: appError.category,
			status: appError.status,
			retryable: appError.retryable,
		});
	}

	// Report server errors to Sentry in production (lazy-loaded)
	if (!import.meta.env.DEV && appError.category === ErrorCategory.SERVER) {
		import("@sentry/tanstackstart-react").then((Sentry) => {
			Sentry.captureException(error, {
				tags: { category: appError.category, status: String(appError.status) },
			});
		});
	}

	// Don't show toast here - let components handle error display
	// This prevents duplicate toasts when using useMutationWithToast
}

/**
 * Auth error should trigger logout
 * This can be called from query error boundaries
 */
export function handleAuthError() {
	const appError = parseQueryError(new Error("Authentication required"));

	if (appError.category === ErrorCategory.AUTH) {
		// Clear auth and redirect to login
		// This will be handled by the auth-client middleware for 401s
		// But we can also handle 403s here if needed
		window.location.href = "/login";
	}
}
